import { useState } from "react";
import React from "react";
import { LanguageItem } from "../common";
import { validatePasswordRequirements } from "../../utils/ValidationUtils";

/**
 * Custom hook for handling password validation including:
 * - Password requirements (length, uppercase, lowercase, number)
 * - Password confirmation matching
 *
 * @returns {object} Validation state and methods
 */
function usePasswordValidation() {
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(false);

  /**
   * Validates password requirements and confirmation match
   *
   * @param {string} password - The new password
   * @param {string} confirmPassword - The password confirmation
   * @returns {boolean} True if validation passes, false otherwise
   */
  function validate(password, confirmPassword) {
    let isValid = true;

    // Reset error states
    setPasswordMismatch(false);
    setPasswordRequirements(false);

    // Check password requirements
    if (!validatePasswordRequirements(password)) {
      setPasswordRequirements(true);
      isValid = false;
    }

    // Check password match
    if (password !== confirmPassword) {
      setPasswordMismatch(true);
      isValid = false;
    }

    return isValid;
  }

  /**
   * Resets all validation states
   */
  function reset() {
    setPasswordMismatch(false);
    setPasswordRequirements(false);
  }

  /**
   * Gets props for password field with requirements validation
   */
  const passwordFieldProps = passwordRequirements
    ? {
        error: true,
        helperText: (
          <React.Fragment>
            <LanguageItem cs>Heslo musí obsahovat alespoň 8 znaků, velké a malé písmeno a číslo.</LanguageItem>
            {/* <LanguageItem en>
              Password must contain at least 8 characters, uppercase and lowercase letter and a number.
            </LanguageItem> */}
          </React.Fragment>
        ),
      }
    : {};

  /**
   * Gets props for password confirmation field with mismatch validation
   */
  const confirmPasswordFieldProps = passwordMismatch
    ? {
        error: true,
        helperText: (
          <React.Fragment>
            <LanguageItem cs>Hesla se neshodují.</LanguageItem>
            {/* <LanguageItem en>Passwords are not matching.</LanguageItem> */}
          </React.Fragment>
        ),
      }
    : {};

  return {
    validate,
    reset,
    passwordFieldProps,
    confirmPasswordFieldProps,
    hasPasswordMismatch: passwordMismatch,
    hasPasswordRequirements: passwordRequirements,
  };
}

export default usePasswordValidation;
